import { Injectable } from '@angular/core';
import { AppNamespace, EmailBuilderTemplate, OwnerType, TemplatesService } from '@vendasta/email-builder';
import { TemplateReferenceInterface } from '@vendasta/email-builder/lib/_internal';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { Observable, catchError, of, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { EmailContentData } from '../types/contentblocks';

@Injectable({
  providedIn: 'root',
})
export class EmailBuilderDataService {
  constructor(
    private readonly svc: TemplatesService,
    private readonly snackbarService: SnackbarService,
  ) {}

  getTemplate$(
    ownerType: OwnerType,
    ownerId: string,
    appNamespace: AppNamespace,
    templateId: string,
  ): Observable<EmailBuilderTemplate> {
    return this.svc.getTemplate(ownerType, ownerId, appNamespace, templateId);
  }

  loadTemplate$(
    ownerType: OwnerType,
    ownerId: string,
    appNamespace: AppNamespace,
    templateId: string,
  ): Observable<EmailContentData> {
    return this.svc.getTemplate(ownerType, ownerId, appNamespace, templateId).pipe(
      map((template) => {
        const parse: EmailContentData = JSON.parse(template.templateJson);
        parse.id = template.templateId;
        parse.name = template.templateDisplayName;
        return parse;
      }),
    );
  }

  createNewTemplate$(
    ownerType: OwnerType,
    ownerId: string,
    appNamespace: AppNamespace,
    templateId: string,
    templateData: EmailContentData,
    templateHTML: string,
    parent?: TemplateReferenceInterface,
  ): Observable<boolean> {
    const validationResult = this.validateTemplate(templateData);
    if (!validationResult) {
      return throwError(() => new Error('failed validation'));
    }
    return this.svc
      .createNewTemplate(
        ownerType,
        ownerId,
        appNamespace,
        templateId,
        templateData.name,
        JSON.stringify(templateData),
        templateHTML,
        templateData.subjectLine,
        parent,
      )
      .pipe(
        catchError((err) => {
          this.snackbarService.openErrorSnack('EMAIL_BUILDER.SAVING.GENERIC_ERROR');
          throw new Error(err);
        }),
        map(() => true),
      );
  }

  validateTemplate(templateData: EmailContentData): boolean {
    if (!templateData) {
      return false;
    }

    if (!templateData.subjectLine || !templateData.name) {
      return false;
    }

    const contentBlocksValid = templateData.emailContentBlocks.every(
      (block) => block.valid || block.valid === undefined,
    );
    const basicSetupValid =
      templateData.basicSetupValid || (templateData.name !== '' && templateData.subjectLine !== '');

    if (basicSetupValid && contentBlocksValid && templateData.emailSetup.isValid !== false) {
      return true;
    }
    this.snackbarService.openErrorSnack('EMAIL_BUILDER.SAVING.TEMPLATE_INVALID_ERROR');
    return false;
  }

  getPreviewImageUrl$(
    ownerType: OwnerType,
    ownerId: string,
    appNamespace: AppNamespace,
    templateId: string,
  ): Observable<string> {
    return this.svc
      .getTemplate(ownerType, ownerId, appNamespace, templateId)
      .pipe(map((template) => template.previewUrl));
  }

  updateExistingTemplate$(
    ownerType: OwnerType,
    ownerId: string,
    appNamespace: AppNamespace,
    templateId: string,
    templateData: EmailContentData,
    templateHTML: string,
  ): Observable<boolean> {
    const validationResult = this.validateTemplate(templateData);
    if (!validationResult) {
      return of(false);
    }
    return this.svc
      .updateExistingTemplate(
        ownerType,
        ownerId,
        appNamespace,
        templateId,
        templateData.name,
        JSON.stringify(templateData),
        templateHTML,
        templateData.subjectLine,
      )
      .pipe(
        catchError((err) => {
          this.snackbarService.openErrorSnack('EMAIL_BUILDER.SAVING.GENERIC_ERROR');
          throw new Error(err);
        }),
        map(() => true),
      );
  }
}
